import { useLayoutEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { LOGO, type Box } from '../brand/logo-geometry';
import { useSession } from '../app/context';
import { brandBox } from '../app/brand';
import { useStore } from '../core/store';

/**
 * The My.txt mark — the supplied artwork, not a typeset imitation.
 *
 * Its outlines come from the original vector (see `scripts/derive-brand.mjs`),
 * split by glyph so the opening can stage letters and words without touching
 * their shapes. Two arrangements exist: the original **lockup** (wordmark and
 * claim on one line, as drawn) and a **stack** for narrow screens, where the
 * claim sits centred beneath the wordmark. Each part keeps its own geometry;
 * only the distance between the two parts changes.
 */

export const BRAND_NAME = 'My.txt — A small editor for big ideas.';

export type BrandLayout = 'lockup' | 'stack';
export type BrandPartName = 'word' | 'claim';

const UNDERLINE = LOGO.underline;
/** The claim's box, including the full stroke of the underline. */
const CLAIM_BOX: Box = (() => {
  const b = LOGO.claim.box;
  const bottom = Math.max(b.y + b.h, UNDERLINE.y + UNDERLINE.width / 2);
  return { x: b.x, y: b.y, w: b.w, h: bottom - b.y };
})();
const WORD_BOX: Box = LOGO.wordmark.box;

interface Arrangement {
  w: number;
  h: number;
  parts: Record<BrandPartName, { x: number; y: number }>;
}

const STACK_GAP = 34;
const STACK_W = Math.max(WORD_BOX.w, CLAIM_BOX.w) + 8;

export const ARRANGEMENTS: Record<BrandLayout, Arrangement> = {
  // Exactly as drawn.
  lockup: { w: LOGO.viewBox.w, h: LOGO.viewBox.h, parts: { word: { x: WORD_BOX.x, y: WORD_BOX.y }, claim: { x: CLAIM_BOX.x, y: CLAIM_BOX.y } } },
  stack: {
    w: STACK_W,
    h: WORD_BOX.h + STACK_GAP + CLAIM_BOX.h,
    parts: { word: { x: (STACK_W - WORD_BOX.w) / 2, y: 0 }, claim: { x: (STACK_W - CLAIM_BOX.w) / 2, y: WORD_BOX.h + STACK_GAP } },
  },
};

const pct = (n: number, of: number) => `${(n / of) * 100}%`;

/** Where a part sits inside the mark, as percentages of the mark's box. */
export function partStyle(layout: BrandLayout, part: BrandPartName): CSSProperties {
  const a = ARRANGEMENTS[layout];
  const box = part === 'word' ? WORD_BOX : CLAIM_BOX;
  const at = a.parts[part];
  return { left: pct(at.x, a.w), top: pct(at.y, a.h), width: pct(box.w, a.w), height: pct(box.h, a.h) };
}

/** The visible part of the mark within its box (the wordmark alone when the claim is set aside). */
export function partFraction(layout: BrandLayout, part: BrandPartName): Box {
  const a = ARRANGEMENTS[layout];
  const box = part === 'word' ? WORD_BOX : CLAIM_BOX;
  const at = a.parts[part];
  return { x: at.x / a.w, y: at.y / a.h, w: box.w / a.w, h: box.h / a.h };
}

const viewBox = (b: Box) => `${b.x} ${b.y} ${b.w} ${b.h}`;

/** The wordmark: My.txt, one path per glyph. */
export function WordPaths() {
  return (
    <svg className="brand-svg" viewBox={viewBox(WORD_BOX)} aria-hidden="true" focusable="false">
      <g className="brand-glyphs">
        {LOGO.wordmark.glyphs.map((g, i) => (
          <path key={i} className="brand-glyph" d={g.d} data-x={((g.box.x + g.box.w / 2 - WORD_BOX.x) / WORD_BOX.w).toFixed(3)} />
        ))}
      </g>
    </svg>
  );
}

/** The claim — A small *editor* for big ideas. — one group per word, and the underline as a drawable line. */
export function ClaimPaths({ only }: { only?: 'editor' }) {
  return (
    <svg className="brand-svg" viewBox={viewBox(CLAIM_BOX)} aria-hidden="true" focusable="false">
      <g className="brand-words">
        {LOGO.claim.words.map((w, i) =>
          only && i !== 2 ? null : (
            <g key={i} className="brand-claim-word" data-word={i} data-editor={i === 2 || undefined}>
              {w.glyphs.map((g, j) => (
                <path key={j} d={g.d} />
              ))}
            </g>
          ),
        )}
      </g>
      <path className="brand-underline" d={`M${UNDERLINE.x1} ${UNDERLINE.y}H${UNDERLINE.x2}`} strokeWidth={UNDERLINE.width} pathLength={1} />
    </svg>
  );
}

interface MarkProps {
  layout: BrandLayout;
  className?: string;
  style?: CSSProperties;
  /** Extra layers the opening uses (glow, trace); the plain mark has none. */
  children?: ReactNode;
  markRef?: React.Ref<HTMLDivElement>;
}

/** The mark as one object: a box of the arrangement's proportion holding its two parts. */
export function BrandMark({ layout, className = '', style, children, markRef }: MarkProps) {
  const a = ARRANGEMENTS[layout];
  return (
    <div ref={markRef} className={`brand-mark ${className}`} data-layout={layout} role="img" aria-label={BRAND_NAME} style={{ aspectRatio: `${a.w} / ${a.h}`, ...style }}>
      {children ?? (
        <>
          <div className="brand-part" data-part="word" style={partStyle(layout, 'word')}>
            <WordPaths />
          </div>
          <div className="brand-part" data-part="claim" style={partStyle(layout, 'claim')}>
            <ClaimPaths />
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The permanent place of the mark: the canvas's bottom left, like a maker's
 * label on an instrument's base. It yields when a window comes close and
 * disappears entirely under a window rather than letting fragments peek out.
 */
export function BrandCorner({ hidden }: { hidden: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const box = useStore(brandBox, (b) => b);
  const state = useSession((s) => brandState(box, s.windows.map((w) => w.rect)));

  // Measure where the visible mark is, so windows can be compared with it.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      const claim = getComputedStyle(el).getPropertyValue('--brand-claim').trim() !== '0';
      const word = partFraction('lockup', 'word');
      const visible = claim ? { x: r.left, y: r.top, w: r.width, h: r.height } : { x: r.left, y: r.top, w: r.width * (word.x + word.w), h: r.height };
      brandBox.set(visible);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <footer className="brand-corner" data-state={state} data-hidden={hidden || undefined} aria-hidden={hidden || undefined}>
      <BrandMark layout="lockup" markRef={ref} className="brand-corner-mark" />
    </footer>
  );
}

/** How the corner mark should read, given the windows around it. */
export function brandState(box: Box | null, rects: ReadonlyArray<{ x: number; y: number; w: number; h: number }>): 'rest' | 'yield' | 'covered' {
  if (!box || !box.w) return 'rest';
  const NEAR = 16;
  let state: 'rest' | 'yield' | 'covered' = 'rest';
  for (const r of rects) {
    const dx = Math.max(box.x - (r.x + r.w), r.x - (box.x + box.w), 0);
    const dy = Math.max(box.y - (r.y + r.h), r.y - (box.y + box.h), 0);
    if (dx === 0 && dy === 0) return 'covered';
    if (Math.hypot(dx, dy) < NEAR) state = 'yield';
  }
  return state;
}
